import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminProfile } from '../api/types';
import { authApi } from '../api/endpoints';
import { configureApiAuth } from '../api/client';
import { decodeJwt, isExpired } from './jwt';

interface AuthState {
  token: string | null;
  profile: AdminProfile | null;
  status: 'unknown' | 'authenticated' | 'unauthenticated';
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hydrate: () => void;
  /** Clear the forced-rotation flag after a successful mandatory change. */
  markPasswordChanged: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      profile: null,
      status: 'unknown',

      login: async (email, password) => {
        const res = await authApi.login(email, password);
        set({ token: res.token, profile: res.admin, status: 'authenticated' });
      },

      logout: () => {
        set({ token: null, profile: null, status: 'unauthenticated' });
      },

      markPasswordChanged: () => {
        const { profile } = get();
        if (profile) set({ profile: { ...profile, mustChangePassword: false } });
      },

      hydrate: () => {
        const { token } = get();
        if (!token) {
          set({ status: 'unauthenticated' });
          return;
        }
        const claims = decodeJwt(token);
        if (!claims || isExpired(claims)) {
          set({ token: null, profile: null, status: 'unauthenticated' });
          return;
        }
        set({ status: 'authenticated' });
      },
    }),
    {
      name: 'pakgames.admin.auth',
      partialize: (s) => ({ token: s.token, profile: s.profile }),
    },
  ),
);

// Wire the API client to the store: it reads the token and, on a code:4,
// clears the session (no refresh token exists on the backend — hard logout).
configureApiAuth(
  () => useAuthStore.getState().token,
  () => useAuthStore.getState().logout(),
);

/** Convenience selector hooks. */
export const useAdminProfile = () => useAuthStore((s) => s.profile);
export const useAdminRole = () => useAuthStore((s) => s.profile?.role ?? null);
